import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User as UserIcon, X } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import type { HubUserSummary } from '../types';

/**
 * Sélecteur de personnes pour la liste nominative d'une application
 * (`accessMode: 'user_list'`).
 *
 * Le répertoire complet des utilisateurs actifs est chargé une seule fois et
 * sert à la fois de source de recherche et de table de correspondance pour
 * afficher le nom des personnes déjà sélectionnées, quel que soit le filtre
 * courant — l'alternative, une requête par identifiant, multiplierait les
 * appels sans rien gagner sur un annuaire de cette taille.
 */

interface UserMultiSelectProps {
    value: string[];
    onChange: (userIds: string[]) => void;
}

export function UserMultiSelect({ value, onChange }: UserMultiSelectProps) {
    const [search, setSearch] = useState('');

    const { data: users = [], isLoading } = useQuery({
        queryKey: ['admin-users', 'picker'],
        queryFn: () => api.get<HubUserSummary[]>('/api/admin/users'),
        staleTime: 60_000,
    });

    const selected = useMemo(
        () =>
            value
                .map((id) => users.find((user) => user.id === id))
                .filter((user): user is HubUserSummary => user !== undefined),
        [value, users]
    );

    const needle = search.trim().toLowerCase();
    const suggestions = useMemo(() => {
        const pool =
            needle === ''
                ? users
                : users.filter(
                      (user) =>
                          user.displayName.toLowerCase().includes(needle) ||
                          user.email.toLowerCase().includes(needle)
                  );
        // Une recherche vide sur un grand annuaire resterait illisible sans
        // cette limite ; taper deux lettres suffit à retomber sous la barre.
        return pool.slice(0, 50);
    }, [users, needle]);

    const toggle = (userId: string) => {
        onChange(value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId]);
    };

    return (
        <div className="space-y-2.5">
            {selected.length > 0 && (
                <ul className="flex flex-wrap gap-1.5" aria-label="Personnes autorisées">
                    {selected.map((user) => (
                        <li key={user.id}>
                            <span className="badge badge-neutral gap-1 pr-1">
                                {user.displayName}
                                <button
                                    type="button"
                                    onClick={() => toggle(user.id)}
                                    className="rounded-full p-0.5 hover:bg-[var(--color-border)]"
                                    aria-label={`Retirer ${user.displayName}`}
                                >
                                    <X className="w-3 h-3" aria-hidden />
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="input"
                placeholder="Chercher une personne par nom ou e-mail"
                aria-label="Chercher une personne"
            />

            <div className="border border-[var(--color-border)] rounded-lg max-h-56 overflow-y-auto">
                {isLoading ? (
                    <p className="px-3 py-4 text-xs text-[var(--color-text-secondary)]">
                        Chargement de l’annuaire…
                    </p>
                ) : suggestions.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-[var(--color-text-secondary)]">
                        Personne ne correspond à cette recherche.
                    </p>
                ) : (
                    <ul>
                        {suggestions.map((user) => {
                            const isSelected = value.includes(user.id);

                            return (
                                <li
                                    key={user.id}
                                    className="border-b border-[var(--color-border)] last:border-b-0"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggle(user.id)}
                                        className={cn(
                                            'flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm',
                                            isSelected
                                                ? 'text-[var(--color-primary)] font-medium'
                                                : 'text-[var(--color-text)] hover:text-[var(--color-primary)]'
                                        )}
                                        aria-pressed={isSelected}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            readOnly
                                            className="pointer-events-none"
                                            tabIndex={-1}
                                        />
                                        <UserIcon
                                            className="w-3.5 h-3.5 shrink-0 text-[var(--color-text-muted)]"
                                            aria-hidden
                                        />
                                        <span className="truncate">{user.displayName}</span>
                                        <span className="shrink-0 ml-auto text-xs text-[var(--color-text-muted)] truncate">
                                            {user.email}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
